<script setup lang="ts">
import { PhCheck } from '@phosphor-icons/vue'
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import type { Props } from './type'

defineOptions({
  name: 'ProfileWorkFilterDropdown',
})

const props = withDefaults(defineProps<Props>(), {
  label: '作品',
})

const emit = defineEmits<{
  activate: []
  'update:modelValue': [value: string]
}>()

const rootRef = ref<HTMLElement | null>(null)
const open = ref(false)
const currentOptionLabel = computed(
  () => props.options.find((option) => option.key === props.modelValue)?.label ?? '',
)
const triggerLabel = computed(() =>
  props.modelValue === 'all' ? props.label : currentOptionLabel.value || props.label,
)
const triggerText = computed(() =>
  typeof props.count === 'number' ? `${props.count} ${triggerLabel.value}` : triggerLabel.value,
)

function closeMenu() {
  open.value = false
}

function handleTriggerClick() {
  if (!props.active) {
    emit('activate')
    closeMenu()
    return
  }

  open.value = !open.value
}

function handleOptionSelect(key: string) {
  if (key !== props.modelValue) {
    emit('update:modelValue', key)
  }

  closeMenu()
}

function handleDocumentPointerDown(event: Event) {
  const target = event.target

  if (!(target instanceof Node) || !rootRef.value?.contains(target)) {
    closeMenu()
  }
}

function handleEscape(event: KeyboardEvent) {
  if (event.key === 'Escape') {
    closeMenu()
  }
}

watch(
  () => props.active,
  (active) => {
    if (!active) {
      closeMenu()
    }
  },
)

watch(open, (isOpen) => {
  if (isOpen) {
    document.addEventListener('pointerdown', handleDocumentPointerDown)
    document.addEventListener('keydown', handleEscape)
    return
  }

  document.removeEventListener('pointerdown', handleDocumentPointerDown)
  document.removeEventListener('keydown', handleEscape)
})

onBeforeUnmount(() => {
  document.removeEventListener('pointerdown', handleDocumentPointerDown)
  document.removeEventListener('keydown', handleEscape)
})
</script>

<template>
  <div ref="rootRef" class="profile-work-filter-dropdown">
    <button
      type="button"
      class="profile-work-filter-dropdown__trigger"
      :class="{ 'is-active': active, 'is-open': open }"
      role="tab"
      :aria-selected="active"
      :aria-expanded="open"
      :aria-label="`${triggerText}，当前筛选${currentOptionLabel || '全部'}`"
      @click="handleTriggerClick"
    >
      <span class="profile-work-filter-dropdown__trigger-text">{{ triggerText }}</span>
      <span class="profile-work-filter-dropdown__trigger-icon" aria-hidden="true">
        <svg viewBox="0 0 12 12" fill="none">
          <path d="M3 4.5L6 7.5L9 4.5" />
        </svg>
      </span>
    </button>

    <transition name="profile-work-filter-dropdown-fade">
      <div v-if="open" class="profile-work-filter-dropdown__content" role="menu" aria-label="作品类型">
        <button
          v-for="option in options"
          :key="option.key"
          type="button"
          class="profile-work-filter-dropdown__item"
          :class="{ 'is-selected': modelValue === option.key }"
          role="menuitemradio"
          :aria-checked="modelValue === option.key"
          @click="handleOptionSelect(option.key)"
        >
          <span>{{ option.label }}</span>
          <PhCheck
            v-if="modelValue === option.key"
            class="profile-work-filter-dropdown__item-check"
            :size="15"
            weight="bold"
          />
        </button>
      </div>
    </transition>
  </div>
</template>

<style scoped>
.profile-work-filter-dropdown {
  position: relative;
}

.profile-work-filter-dropdown__trigger {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 12px 12px 8px;
  color: var(--profile-muted);
  font-size: 16px;
  font-weight: 500;
  line-height: 22px;
  transition: color var(--app-dropdown-motion-duration) ease;
}

.profile-work-filter-dropdown__trigger.is-active {
  color: var(--profile-text);
}

.profile-work-filter-dropdown__trigger.is-active::after {
  position: absolute;
  right: 12px;
  bottom: 3px;
  left: 12px;
  height: 1px;
  border-radius: 999px;
  background: var(--profile-text);
  content: '';
}

.profile-work-filter-dropdown__trigger-icon {
  display: inline-flex;
  width: 12px;
  height: 12px;
  align-items: center;
  justify-content: center;
  color: currentColor;
  opacity: 0.78;
  transition:
    opacity var(--app-dropdown-motion-duration) ease,
    transform var(--app-dropdown-motion-duration) ease;
}

.profile-work-filter-dropdown__trigger-icon svg {
  display: block;
  width: 10px;
  height: 10px;
}

.profile-work-filter-dropdown__trigger-icon path {
  stroke: currentColor;
  stroke-width: 1.6;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.profile-work-filter-dropdown__trigger.is-open .profile-work-filter-dropdown__trigger-icon {
  transform: rotate(180deg);
}

.profile-work-filter-dropdown__content {
  position: absolute;
  top: calc(100% + 8px);
  left: 50%;
  z-index: 16;
  display: grid;
  min-width: 136px;
  gap: 0;
  padding: var(--app-dropdown-padding);
  border: 1px solid var(--border-dropdown);
  border-radius: var(--app-dropdown-radius);
  background: var(--bg-dropdown);
  box-shadow: var(--app-shadow-floating);
  transform: translateX(-50%);
}

.profile-work-filter-dropdown__item {
  display: flex;
  min-width: 0;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: var(--app-dropdown-item-min-height);
  border-radius: var(--app-dropdown-item-radius);
  padding: var(--app-dropdown-item-padding-y) var(--app-dropdown-item-padding-x);
  color: var(--profile-text);
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  text-align: left;
  transition:
    background-color var(--app-dropdown-motion-duration) ease,
    color var(--app-dropdown-motion-duration) ease;
}

.profile-work-filter-dropdown__item:hover {
  background-color: var(--app-state-layer-hover);
}

.profile-work-filter-dropdown__item:active {
  background-color: var(--app-state-layer-pressed);
}

.profile-work-filter-dropdown__item-check {
  flex: 0 0 auto;
}

.profile-work-filter-dropdown-fade-enter-active,
.profile-work-filter-dropdown-fade-leave-active {
  transition:
    opacity var(--app-dropdown-motion-duration) var(--app-dropdown-motion-ease),
    transform var(--app-dropdown-motion-duration) var(--app-dropdown-motion-ease);
}

.profile-work-filter-dropdown-fade-enter-from,
.profile-work-filter-dropdown-fade-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(-6px) scale(0.98);
}

.profile-work-filter-dropdown-fade-enter-to,
.profile-work-filter-dropdown-fade-leave-from {
  opacity: 1;
  transform: translateX(-50%) translateY(0) scale(1);
}

@media (max-width: 640px) {
  .profile-work-filter-dropdown__trigger {
    padding: 10px 12px 6px;
    font-size: 15px;
    line-height: 22px;
  }

  .profile-work-filter-dropdown__trigger.is-active::after {
    bottom: 2px;
  }
}
</style>
